export interface Guide {
  id: string;
  title: string;
  situation: string;
  whatToSay: string[];
  whatNotToSay: string[];
  practicalSteps: string[];
  followUpTemplate: string;
}

export const accommodationGuides: Guide[] = [
  {
    id: 'flexible-hours',
    title: 'Flexible Working Hours',
    situation:
      'Employee requests schedule changes due to sleep disruption, fatigue, or symptom timing.',
    whatToSay: [
      "I'm open to exploring schedule flexibility. Let's figure out what works for both of us.",
      "What hours work best for you right now? Let's see how we can make that fit.",
      'Would a trial period of [X weeks] help us figure out the best arrangement?',
    ],
    whatNotToSay: [
      "I can't make exceptions for you.",
      'Everyone has to be here 9-5.',
      'Why do you need to change your hours?',
    ],
    practicalSteps: [
      'Identify core collaboration hours the team needs (e.g., 10 AM - 2 PM)',
      'Allow flexibility outside core hours',
      'Establish a communication protocol for variable days (e.g., Slack message by 8 AM)',
      'Set a trial period (4 weeks recommended)',
      'Schedule a review meeting at the end of the trial',
      'Document the arrangement (for consistency, not surveillance)',
    ],
    followUpTemplate:
      "It's been [X weeks] since we adjusted your schedule. How is it working for you? Is there anything we should tweak?",
  },
  {
    id: 'temperature-environment',
    title: 'Temperature & Environment',
    situation:
      'Employee experiences hot flashes or temperature sensitivity that affects comfort and concentration.',
    whatToSay: [
      'Would it help to sit near a window or in a spot with better airflow?',
      'I can look into getting a desk fan or adjusting the AC in your area.',
      'If you ever need to step away for a few minutes to cool down, that\'s perfectly fine.',
    ],
    whatNotToSay: [
      'You always look so hot and bothered.',
      "Everyone else is cold — you're the only one who's warm.",
      "Can't you just dress lighter?",
    ],
    practicalSteps: [
      'Offer a desk fan (personal, quiet)',
      'Review seating — near windows, away from heating vents, near exits',
      'Keep meeting rooms cooler or ensure access to cooler spaces',
      'Allow breaks to step outside or to a cooler area',
      'Dress code flexibility (if applicable)',
      'Keep cold water accessible in meeting rooms',
    ],
    followUpTemplate:
      'How are the environment changes working out? Let me know if there\'s anything else we can adjust.',
  },
  {
    id: 'meeting-accommodations',
    title: 'Meeting Accommodations',
    situation:
      'Employee finds long meetings difficult due to concentration issues, hot flashes, or anxiety.',
    whatToSay: [
      'Would it help to build regular breaks into our longer meetings?',
      'Feel free to step out briefly during any meeting — no need to explain.',
      'I can share meeting agendas in advance so you can prepare.',
    ],
    whatNotToSay: [
      'You seem really distracted in meetings.',
      "Are you sure you're up for this presentation?",
      'Maybe someone else should take the client calls for now.',
    ],
    practicalSteps: [
      'Keep meetings to 45 minutes max when possible; schedule 5-minute breaks for longer ones',
      'Share agendas 24 hours in advance',
      'Offer the choice of camera-off for video calls',
      'Allow standing/moving during meetings',
      'Position near the door for easy exit',
      'Provide meeting notes or recordings for reference',
    ],
    followUpTemplate:
      "I've been trying to keep meetings shorter and build in breaks. Is that helping? Any other suggestions?",
  },
  {
    id: 'workload-adjustment',
    title: 'Workload Adjustment',
    situation:
      'Employee is struggling with brain fog, fatigue, or concentration that temporarily affects productivity.',
    whatToSay: [
      "Let's look at your current workload and see if we can adjust priorities.",
      'Would it help to break larger projects into smaller milestones?',
      "What time of day do you feel most focused? Let's try to schedule demanding work then.",
    ],
    whatNotToSay: [
      'Your performance has really dropped.',
      "I'm going to have to give your project to someone else.",
      'You need to find a way to manage this — the work still needs to get done.',
    ],
    practicalSteps: [
      'Review current tasks and identify which can be deferred or delegated',
      'Break large projects into smaller, manageable milestones',
      "Align cognitively demanding work with the employee's best hours",
      'Reduce multitasking requirements',
      'Allow more time for deadline-sensitive work',
      'Check in weekly (not daily — avoid micromanaging)',
    ],
    followUpTemplate:
      "How are you finding the adjusted workload? Let's review priorities together and make sure you feel on track.",
  },
  {
    id: 'general-conversation',
    title: 'General Conversation Framework',
    situation:
      'Universal framework for any accommodation conversation.',
    whatToSay: [
      'Thank you for being open with me. I want to make sure you have what you need to do your best work here.',
      "What would help you most right now? Focus on what you CAN change: schedule, environment, workload, communication.",
      "You don't need to share any more details than you're comfortable with. I just need to know what adjustments would help.",
      "This conversation is between us. If we need to involve HR for formal accommodations, I'll discuss that with you first.",
      "I'll check in with you in [timeframe]. If anything changes before then, please don't hesitate to reach out.",
      'I appreciate you talking to me about this. I want to make sure this is a place where you can thrive.',
    ],
    whatNotToSay: [
      "My sister/wife/friend went through that...",
      'Have you tried [medical advice]?',
      "I'll make a note of it.",
      "Let me know if you need anything. (Too vague — be specific.)",
    ],
    practicalSteps: [
      'Let the employee lead the conversation',
      "Don't fill silences — give them space",
      'Resist the urge to share personal anecdotes or offer medical advice',
      'Focus on workplace adjustments, not diagnosis',
      'Confirm confidentiality explicitly',
      'Follow up within the agreed timeframe',
    ],
    followUpTemplate:
      "I wanted to check in since our last conversation. How are things going? Is there anything we should adjust?",
  },
];
